import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { apiPost, apiRequest, request } from "../../utils/request";
import Select from "react-select";
import ReactTable from "react-table";
import { Button } from "reactstrap";
import moment from "moment";
import {
  BASE_URL,
  getIsBaseDropOff,
  isNullEmptyOrWhitespace,
  IsNullOrUndefined,
  LOCATION_TYPE_BASE_CODE,
  LOCATION_TYPE_DROPOFF_CODE,
  TextInputParseInt,
} from "../../constants";
import { every } from "lodash";
import { toastr } from "react-redux-toastr";
import FileSaver from "file-saver";
import CheckBoxControl from "../Common/CheckBoxControl";
import { mustBeArray } from "../../utils/lib";

/**
 * @typedef {Object} IAuditTankLocationRequest
 * @property {number} id
 * @property {number} locationId
 * @property {string} locationName
 * @property {string} tankName
 * @property {number} tankId
 * @property {number | null} pickupId
 * @property {number | null} runId
 * @property {number} tankActionType
 * @property {string} tankActionTypeString
 * @property {string} createdDate
 * @property {string} createdByUserName
 * @property {string} actionCompleteDate
 * @property {string} completedByUserName
 * @property {string} tankAssignedDate
 * @property {string} tankAssignedByUserName
 * @property {boolean} hasHeater
 */

/**
 * @typedef {Object} IBaseAssignedTank
 * @property {number} locationId
 * @property {string} locationName
 * @property {string} locationTypeUniqueCode
 * @property {number} tankSizeId
 * @property {string} tankName
 * @property {number} capacityLitres
 * @property {number} activeCount
 * @property {number} inactiveCount
 * @property {number} pendingCount
 * @property {boolean} hasHeater
 * @property {string} tankAssetType
 */

/**
 * @typedef {Object} AssetTypeOption
 * @property {number} id
 * @property {string} name
 * @property {string} type
 */

/**
 * @typedef {Object} SelectOption
 * @property {number} value
 * @property {string} label
 */

/**
 * @typedef {Object} TankToGenerate
 * @property {SelectOption | null} tank
 * @property {SelectOption | null} location
 * @property {SelectOption | null} type
 */

/**
 * @typedef {Object} ITankToPrint
 * @property {number} tankSizeId
 * @property {string} tankName
 * @property {number} quantity
 * @property {number} locationId
 * @property {string} locationName
 * @property {number} assetTypeId
 * @property {string} typeName
 */

/**
 *
 * @returns {TankToGenerate}
 */
const initialTankToGenerate = () => {
  return {
    tank: null,
    location: null,
    type: null,
  };
};

class TankAssignmentPage extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.state = {
      /**
       * @type {SelectOption[]}
       */
      availableTanks: [],
      /**
       * @type {IBaseAssignedTank[]}
       */
      baseAssignedTanks: [],
      /**
       * @type {ITankToPrint[]}
       */
      tanksToGenerate: [],
      selectedTank: null,
      /**
       * @type {SelectOption[]}
       */
      locations: [],
      tankToGenerate: initialTankToGenerate(),
      printOffset: 0,
      newTanksOnly: false,
      auditLoaded: false,
      /**
       * @type {IAuditTankLocationRequest[]}
       */
      auditTanksLocationRequests: [],
      loadingAudit: false,
      /**
       * @type {SelectOption[]}
       */
      tankTypeOptions: [],
      /**
       * @type {SelectOption[]}
       */
      assetTypes: [],
    };
    this.fetchTanks = this.fetchTanks.bind(this);
    this.getBaseAssignedTanksColumns =
      this.getBaseAssignedTanksColumns.bind(this);
    this.fetchBaseAssignedTanks = this.fetchBaseAssignedTanks.bind(this);
    this.handleAddSelectedTank = this.handleAddSelectedTank.bind(this);
    this.handleFieldChange = this.handleFieldChange.bind(this);
    this.renderQuantity = this.renderQuantity.bind(this);
    this.getTanksToGenerate = this.getTanksToGenerate.bind(this);
    this.fetchBaseDropOffLocations = this.fetchBaseDropOffLocations.bind(this);
    this.handleRemoveGenerateTank = this.handleRemoveGenerateTank.bind(this);
    this.handleAssignAndPrint = this.handleAssignAndPrint.bind(this);
    this.fetchAuditLocationTankRequests =
      this.fetchAuditLocationTankRequests.bind(this);
    this.getAuditTanksLocationRequestsColumns =
      this.getAuditTanksLocationRequestsColumns.bind(this);
    this.loadReloadAudit = this.loadReloadAudit.bind(this);
    this.fetchAssetTypes = this.fetchAssetTypes.bind(this);
    this.getTankToGenerate = this.getTankToGenerate.bind(this);
  }

  /**
   * @param {TankToGenerate} tank
   * @param {number=} quantity
   * @returns {ITankToPrint}
   */
  getTankToGenerate({ tank, location, type }, quantity = 0) {
    return {
      tankSizeId: tank.value,
      tankName: tank.label,
      quantity: quantity,
      locationId: location.value,
      locationName: location.label,
      assetTypeId: type.value,
      typeName: type.label,
    };
  }

  fetchTanks() {
    apiRequest("/api/tanks/alltanks").then((x) => {
      if (!Array.isArray(x.data)) return;
      this.setState({
        availableTanks: x.data.map((y) => {
          return {
            value: y.tankSizeId,
            label: `${y.tankTypeName} (${y.maxCapacityLitres}L / ${y.pickupCapacityLitres}L)`,
          };
        }),
      });
    });
  }

  fetchBaseAssignedTanks() {
    apiRequest("/api/tanks/baseAssignedTanks").then((x) => {
      if (!Array.isArray(x.data)) return;
      this.setState({ baseAssignedTanks: Array.from(x.data) });
    });
  }

  fetchAuditLocationTankRequests() {
    apiRequest("/api/tanks/auditLocationTankRequests").then((x) => {
      if (!Array.isArray(x.data)) return;
      this.setState({
        auditTanksLocationRequests: [...x.data],
        auditLoaded: true,
        loadingAudit: false,
      });
    });
  }

  fetchAssetTypes() {
    request
      .get("/api/tanks/lookups/tank-asset-types")
      .then(
        (/** @type {import("axios").AxiosResponse<AssetTypeOption[]>} */ x) => {
          if (x.data) {
            this.setState({
              assetTypes: mustBeArray(x.data).map((v) => {
                return {
                  value: v.id,
                  label: `${v.type} - ${v.name}`,
                };
              }),
            });
          }
        }
      );
  }

  loadReloadAudit() {
    this.setState({ loadingAudit: true }, () => {
      this.fetchAuditLocationTankRequests();
    });
  }

  fetchBaseDropOffLocations() {
    apiRequest(`/api/managelocations?locationTypes=B&locationTypes=D`).then(
      (x) => {
        if (Array.isArray(x.data)) {
          this.setState({
            locations: [
              ...x.data.map((y) => {
                return {
                  label: `${y.locationName} (${getIsBaseDropOff(y)})`,
                  value: y.id,
                };
              }),
            ],
          });
        }
      }
    );
  }

  /**
   * @returns {Array<import("react-table").Column<IBaseAssignedTank>>}
   */
  getBaseAssignedTanksColumns() {
    return [
      {
        id: "locationName",
        accessor: (x) => x.locationName,
        Header: "Location",
        maxWidth: 300,
      },
      {
        id: "locationTypeUniqueCode",
        Header: "Location Type",
        accessor: (x) => getIsBaseDropOff(x),
        maxWidth: 100,
      },
      {
        id: "tankName",
        Header: "Tank Name",
        accessor: (x) => x.tankName,
      },
      {
        id: "tankAssetType",
        Header: "Asset Type",
        accessor: (x) => x.tankAssetType,
      },
      {
        id: "capacityLitres",
        Header: "Capacity",
        accessor: (x) => x.capacityLitres,
        maxWidth: 80,
      },
      {
        id: "hasHeater",
        Header: "Heater",
        accessor: (x) => (x.hasHeater ? "Yes" : "No"),
        maxWidth: 80,
      },
      {
        id: "pendingCount",
        Header: "Pending",
        accessor: (x) => x.pendingCount,
        maxWidth: 100,
      },
      {
        id: "activeCount",
        Header: "Active",
        accessor: (x) => x.activeCount,
        maxWidth: 100,
      },
      {
        id: "inactiveCount",
        Header: "Inactive",
        accessor: (x) => x.inactiveCount,
        maxWidth: 100,
      },
    ];
  }

  /**
   * @returns {Array<import("react-table").Column<IAuditTankLocationRequest>>}
   */
  getAuditTanksLocationRequestsColumns() {
    return [
      {
        id: "locationName",
        accessor: (x) => x.locationName,
        Cell: (row) => (
          <a
            href={`${BASE_URL}/manage/locations/${row.original.locationId}`}
            target={"_blank"}
          >
            {row.value}
          </a>
        ),
        Header: "Location",
        width: 150,
      },
      {
        id: "tankName",
        Header: "Tank Name",
        accessor: (x) => x.tankName,
        Cell: (row) =>
          IsNullOrUndefined(row.original.tankId) ? (
            <span>{row.value}</span>
          ) : (
            <a
              href={`${BASE_URL}/manage/tanks/${row.original.tankId}`}
              target={"_blank"}
            >
              {row.value}
            </a>
          ),
        width: 300,
      },
      {
        id: "tankActionTypeString",
        Header: "Action",
        accessor: (x) => x.tankActionTypeString,
      },
      {
        id: "hasHeater",
        Header: "Heater",
        accessor: (x) => (x.hasHeater ? "Yes" : "No"),
        width: 80,
      },
      {
        id: "createdDate",
        Header: "Created",
        accessor: (x) => moment(x.createdDate).format("DD/MM/YYYY"),
      },
      {
        id: "createdByUserName",
        Header: "Created By",
        accessor: (x) => x.createdByUserName,
      },
      {
        id: "tankAssignedDate",
        Header: "Assigned",
        accessor: (x) =>
          IsNullOrUndefined(x.tankAssignedDate)
            ? ""
            : moment(x.tankAssignedDate).format("DD/MM/YYYY"),
      },
      {
        id: "tankAssignedByUserName",
        Header: "Assigned By",
        accessor: (x) => x.tankAssignedByUserName,
      },
      {
        id: "actionCompleteDate",
        Header: "Completed",
        accessor: (x) =>
          IsNullOrUndefined(x.actionCompleteDate)
            ? ""
            : moment(x.actionCompleteDate).format("DD/MM/YYYY"),
      },
      {
        id: "CompletedByUserName",
        Header: "Completed By",
        accessor: (x) => x.completedByUserName,
      },
    ];
  }

  handleFieldChange(e, row) {
    const { tanksToGenerate } = this.state;

    const val = TextInputParseInt(e.target.value);

    const updatedTanksToGenerate = [
      ...tanksToGenerate.map((x) => {
        if (
          x.tankSizeId === row.original.tankSizeId &&
          x.locationId === row.original.locationId &&
          x.assetTypeId === row.original.assetTypeId
        ) {
          return { ...x, quantity: val };
        }
        return x;
      }),
    ];

    this.setState({ tanksToGenerate: updatedTanksToGenerate });
  }

  handleRemoveGenerateTank(row) {
    let { tanksToGenerate } = this.state;
    this.setState({
      tanksToGenerate: [
        ...tanksToGenerate.filter(
          (x) =>
            x.tankSizeId !== row.original.tankSizeId ||
            x.locationId !== row.original.locationId ||
            x.assetTypeId !== row.original.assetTypeId 
        ),
      ],
    });
  }

  renderQuantity(row) {
    return (
      <input
        value={row.value}
        type={"number"}
        className={"form-control"}
        style={{ width: 80 }}
        onChange={(e) => this.handleFieldChange(e, row)}
      />
    );
  }

  /**
   * @returns {Array<import("react-table").Column<ITankToPrint>>}
   */
  getTanksToGenerate() {
    return [
      {
        id: "tankName",
        accessor: (x) => x.tankName,
        Header: "Tank Name",
        maxWidth: 300,
      },
      {
        id: "quantity",
        Header: "Quantity",
        accessor: (x) => x.quantity,
        Cell: this.renderQuantity,
        maxWidth: 100,
      },
      {
        id: "locationName",
        Header: "Location",
        accessor: (x) => x.locationName,
      },
      {
        Header: "Asset Type",
        accessor: "typeName",
      },
      {
        id: "actions",
        Header: "",
        Cell: (row) => (
          <span>
            <Button
              color={"danger"}
              onClick={() => this.handleRemoveGenerateTank(row)}
            >
              <i className={"fa fa-times"} />
            </Button>
          </span>
        ),
      },
    ];
  }

  handleAssignAndPrint() {
    let { tanksToGenerate, printOffset, newTanksOnly } = this.state;
    let postData = {
      tanks: [...tanksToGenerate],
      printOffset: isNullEmptyOrWhitespace(printOffset) ? 0 : printOffset,
      newTanksOnly,
    };

    toastr.info("Assigning and generating tanks");

    apiPost("/api/tanks/assignAndGenerate", postData, false).then((x) => {
      x.data.blob().then((blob) => {
        FileSaver.saveAs(
          blob,
          `${moment().format("YYYYMMDD_HHMMSS")}_tank_labels.pdf`
        );
        toastr.success("Created PDF labels!");

        this.fetchBaseAssignedTanks();
      });
    });
  }

  handleAddSelectedTank() {
    const { tankToGenerate, tanksToGenerate } = this.state;

    /**
     * @type {ITankToPrint[]}
     */
    let updatedTanksToGenerate = [];
    /**
     *
     * @param {ITankToPrint} x
     * @returns
     */
    const compareTankToGenerate = (x) =>
      x.tankSizeId === tankToGenerate.tank.value &&
      x.locationId === tankToGenerate.location.value &&
      x.assetTypeId === tankToGenerate.type.value;
    const existingTank = tanksToGenerate.find(compareTankToGenerate);
    
    if (existingTank) {
      updatedTanksToGenerate = tanksToGenerate.map((x) => {
        if (compareTankToGenerate(x)) {
          return {
            ...x,
            quantity: ++x.quantity,
          };
        }
        return x;
      });
      toastr.info("Tank already added", `Tank ${tankToGenerate.tank.label} is already in the list to print. Quantity has been increased instead`);
    } else {
      updatedTanksToGenerate = [
        ...tanksToGenerate,
        this.getTankToGenerate(tankToGenerate),
      ];
    }

    this.setState({
      tanksToGenerate: updatedTanksToGenerate,
      tankToGenerate: initialTankToGenerate(),
    });
  }

  componentDidMount() {
    this.fetchTanks();
    this.fetchBaseAssignedTanks();
    this.fetchBaseDropOffLocations();
    this.fetchAssetTypes();
  }

  render() {
    let {
      availableTanks,
      baseAssignedTanks,
      tanksToGenerate,
      locations,
      tankToGenerate,
      printOffset,
      newTanksOnly,
      auditTanksLocationRequests,
      auditLoaded,
      loadingAudit,
      assetTypes,
    } = this.state;
    /*
            Get tanks that can be assigned
            Select 1 or many tanks 
            Create a batch assignment to a set location, in this case the base location for SRS, this should actually be base, instead of drop-off
            We need a way of not losing these generated tanks...
            Perhaps this page should show all tanks that are assigned to X locations, in this case the Pickup and the Base, these are therefore transferable
            Display all tanks for X locations, filter by type, have a generate, 
            
            Aggregate tanks by tanksizeid then enter the number required....
        
         */

    return (
      <div>
        <form>
          <div className={"row"}>
            <div className={"col-12"}>
              <h4>Tanks to Asssign</h4>
              <p>
                Select the tank and location for which the tank will be
                dispatched from, then assign the quantites
              </p>
              <p>Tanks currently assigned are for reference and re-printing</p>
            </div>
          </div>
          <div className={"form-group row"}>
            <label className={"col-2 col-form-label"}>Tank</label>
            <div className={"col-6"}>
              <Select
                name="tanks"
                value={tankToGenerate.tank}
                multi={false}
                clearable={true}
                simpleValue={false}
                options={availableTanks}
                onChange={(val) =>
                  this.setState({
                    tankToGenerate: { ...tankToGenerate, tank: val },
                  })
                }
              />
            </div>
          </div>
          <div className={"form-group row"}>
            <label className={"col-2 col-form-label"}>Location</label>
            <div className={"col-6"}>
              <Select
                name="locations"
                value={tankToGenerate.location}
                multi={false}
                clearable={true}
                simpleValue={false}
                options={locations}
                onChange={(val) =>
                  this.setState({
                    tankToGenerate: { ...tankToGenerate, location: val },
                  })
                }
              />
            </div>
          </div>
          <div className={"form-group row"}>
            <label className={"col-2 col-form-label"}>Asset Type</label>
            <div className={"col-6"}>
              <Select
                name="locations"
                value={tankToGenerate.type}
                multi={false}
                clearable={true}
                simpleValue={false}
                options={assetTypes}
                onChange={(val) =>
                  this.setState({
                    tankToGenerate: { ...tankToGenerate, type: val },
                  })
                }
              />
            </div>
          </div>
          <div className={"form-group row"}>
            <div className={"offset-2 col-6"}>
              <Button
                color={"primary"}
                disabled={
                  IsNullOrUndefined(tankToGenerate.location) ||
                  IsNullOrUndefined(tankToGenerate.tank) ||
                  IsNullOrUndefined(tankToGenerate.type)
                }
                onClick={this.handleAddSelectedTank}
              >
                Add to Print
              </Button>
            </div>
          </div>

          <div className={"row mt-2"}>
            <div className={"col-12"}>
              <h4>Tanks to print</h4>
              <ReactTable
                sortable
                filterable={false}
                showPagination={false}
                data={tanksToGenerate}
                columns={this.getTanksToGenerate()}
                minRows={5}
              />
            </div>
          </div>
          <div className={"form-group row mt-2"}>
            <label className={"col-2 col-form-label"}>Print Offset</label>
            <div className={"col-2"}>
              <input
                type={"number"}
                className={"form-control"}
                value={printOffset}
                onChange={(e) =>
                  this.setState({
                    printOffset: TextInputParseInt(e.target.value),
                  })
                }
              />
            </div>
          </div>

          <CheckBoxControl
            name="newTanksOnly"
            value={newTanksOnly}
            onChange={(e) => this.setState({ newTanksOnly: e.target.checked })}
            labelText={"New Tanks Only"}
          />

          <div className={"row mt-2"}>
            <div className={"col-12"}>
              <Button
                color={"primary"}
                disabled={
                  tanksToGenerate.length === 0 ||
                  !every(tanksToGenerate, (x) => x.quantity > 0)
                }
                onClick={this.handleAssignAndPrint}
              >
                Assign and Print
              </Button>
              <Button
                className={"ml-2"}
                color={"default"}
                disabled={
                  tanksToGenerate.length === 0 ||
                  !every(tanksToGenerate, (x) => x.quantity > 0)
                }
                onClick={() => this.setState({ tanksToGenerate: [] })}
              >
                Reset
              </Button>
            </div>
          </div>
        </form>

        <div className={"row mt-2"}>
          <div className={"col-12"}>
            <h4>Tanks currently assigned to base or drop off locations</h4>
            <p>
              The active tanks at the selected locations in the tanks to print
              will first used from the below, before generating new tanks
            </p>
            <ReactTable
              sortable
              filterable={false}
              showPagination={true}
              data={baseAssignedTanks}
              columns={this.getBaseAssignedTanksColumns()}
              minRows={5}
            />
          </div>
        </div>

        <div className={"row mt-2"}>
          <div className={"col-12"}>
            <h4>
              Top <b>1000</b> tank request audit trail
              <Button
                className={"ml-1"}
                color={"info"}
                onClick={this.loadReloadAudit}
                disabled={loadingAudit}
              >
                {auditLoaded ? "Reload" : "Load"} Audit
              </Button>
            </h4>
            <ReactTable
              sortable
              filterable={false}
              showPagination={true}
              data={auditTanksLocationRequests}
              columns={this.getAuditTanksLocationRequestsColumns()}
              minRows={5}
            />
          </div>
        </div>
      </div>
    );
  }
}

TankAssignmentPage.propTypes = {
  account: PropTypes.object,
};

function mapStateToProps(state, ownProps) {
  return {
    account: state.account,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(TankAssignmentPage);
